import { useState } from 'react';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { ReactComponent as ErrorIcon } from 'assets/images/notifications/error.svg';
import { ReactComponent as InfoIcon } from 'assets/images/notifications/info.svg';
import { ReactComponent as SuccessIcon } from 'assets/images/notifications/success.svg';
import { ReactComponent as CloseIcon } from 'component-lib/atoms/icon-v2/CloseIcon.svg';
import { NotificationType } from 'core/context/Notifications';

interface PopupProps {
  type: NotificationType;
  visible: boolean;
}

const Popup = styled.div<PopupProps>`
  // We want to override the parent's pointer-events: none
  pointer-events: auto;
  display: ${props => (props.visible ? 'block' : 'none')};

  background: ${props => {
    switch (props.type) {
      case 'error':
        return '#eb3c1a';
      case 'info':
        return '#8706ba';
      case 'success':
        return '#00a878';
    }
  }};

  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border-radius: 3px;
  margin: 0 0 20px 0;
  opacity: 1;
  width: calc(100vw - 40px);

  position: relative;

  @media (min-width: 768px) {
    width: auto;
    min-width: 400px;
    max-width: 800px;
  }
`;

const PopupDetails = styled.div`
  display: flex;
  padding: 20px;
`;

const Left = styled.div`
  flex: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 15px;
`;

const Right = styled.div`
  flex: 1;
  padding: 0 50px 0 0;
  box-sizing: border-box;
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 600;
  ${tw`text-white`}
  margin: 0 0 2px;
`;

const Message = styled.div`
  font-size: 14px;
  font-weight: 600;
  ${tw`text-white`}
  padding: 0 30px 0 0;
  box-sizing: border-box;
  margin-right: 5px;
`;

const Close = styled(CloseIcon)`
  ${tw`text-white`}
  position: absolute;
  right: 20px;
  width: 16px;
  height: 16px;
  margin: auto;
  top: 0;
  bottom: 0;
  cursor: pointer;
`;

interface ProgressBarProps {
  type: NotificationType;
  index: number;
  duration: number;
  playing: boolean;
}

const ProgressBar = styled.div<ProgressBarProps>`
  width: 100%;
  height: 5px;
  opacity: 0.8;
  animation: progress-${props => props.index} ${props => (props.duration ? props.duration : 5)}s linear 1;
  animation-play-state: ${props => (props.playing ? 'running' : 'paused')};

  background: ${props => {
    switch (props.type) {
      case 'error':
        return '#b42a0f';
      case 'info':
        return '#3c11d1';
      case 'success':
        return '#007e5a';
    }
  }};

  @keyframes progress-${props => props.index} {
    0% {
      width: 100%;
    }
    100% {
      width: 0;
    }
  }
`;

interface NotificationProps {
  id: number;
  type: NotificationType;
  title: string;
  duration?: number;
  message: string;
  onClose(): void;
}

export const Notification = ({ type, title, message, duration, id, onClose }: NotificationProps) => {
  const [running, setRunning] = useState(true);

  return (
    <Popup type={type} onMouseEnter={() => setRunning(false)} onMouseLeave={() => setRunning(true)} visible>
      <PopupDetails>
        <Left>
          {type === 'error' && <ErrorIcon tw="w-2" />}
          {type === 'info' && <InfoIcon tw="w-2" />}
          {type === 'success' && <SuccessIcon tw="w-2" />}
        </Left>

        <Right>
          <Title>{title}</Title>
          <Message>{message}</Message>
        </Right>

        <Close onClick={onClose} />
      </PopupDetails>

      {!!duration && (
        <ProgressBar type={type} duration={duration} playing={running} index={id} onAnimationEnd={onClose} />
      )}
    </Popup>
  );
};
